/**
 * Who is liberado for the unit/departamento on screen, for the Visitas and Prestadores tabs.
 *
 * Pulled out of LocationDetailView because the rule had a hole nobody could see from the markup:
 * the list excluded `kind === 'always'`, since permanent clearances had their own orange "Sempre
 * Habilitados" box above it — and that box required `is_authorized`, which a liberação does NOT
 * have. A liberação is a mark recorded ahead of time; the gate authorises it when the person
 * turns up (models/liberacao.go says so, and so does every other surface). So a "Liberação
 * permanente" granted for next April belonged to neither list: the box filtered it out for being
 * unauthorised, the list for being permanent. It showed as LIBERADO in the person modal and in
 * Visitantes Ativos, and the unit's own tab said LIBERADOS 0.
 *
 * One list now, as on the unit drawer of the Cadastro page: "sempre habilitado" is a KIND of
 * liberação, not a different question, and the row already carries its own label
 * ("Liberação permanente") from the shared summary.
 */

/**
 * Is this role a PRESTADOR, for the purpose of which tab it belongs on?
 *
 * `role_metadata.category` first, exactly as `liberacao.isServiceProvider` decides it
 * (utils/liberacoes) — a prestador liberação is written as a `visitor` role with the category
 * beside it, which is why the role type alone is never the answer.
 *
 * Reported 2026-10-19: unit 188's Visitas tab said "LIBERADOS 0" while listing, two inches below,
 * the four people the Visitantes page showed as liberados. The visit list split on `role_type` and
 * the liberados/histórico sections on the category, so a prestador registered as a `visitor` role
 * had its visits on VISITAS and its clearance on PREST — one person's answer on two tabs. Both
 * splits go through here now, so a tab can no longer answer half the question.
 */
export function isPrestadorVisit(
  role:
    | { role_type?: string; role_metadata?: { category?: string; [key: string]: unknown } | null }
    | null
    | undefined,
): boolean {
  if (!role) return false;
  return role.role_metadata?.category === 'service_provider' || role.role_type === 'service_provider';
}

export interface LiberadoCandidate {
  /** The shared summary (utils/liberacoes). Absent = not a clearance at all. */
  liberacao?: { kind?: string; isServiceProvider?: boolean } | null;
  /** Visitor state machine: somebody already inside is shown by the live list above. */
  state?: string;
}

/**
 * Every live clearance for this location, in the order the caller gave them.
 *
 * `inside` is deliberately excluded: that person is in the condomínio right now and appears in
 * the live list above — repeating them as "liberado" reads as a second, separate visit.
 */
export function liberadosForLocation<T extends LiberadoCandidate>(visits: T[], spVisits: T[]): T[] {
  return [...visits, ...spVisits].filter((v) => !!v.liberacao && v.state !== 'inside');
}

/** The Visitas tab's half. */
export function liberadosVisitantes<T extends LiberadoCandidate>(rows: T[]): T[] {
  return rows.filter((v) => !v.liberacao?.isServiceProvider);
}

/** The Prestadores tab's half. */
export function liberadosPrestadores<T extends LiberadoCandidate>(rows: T[]): T[] {
  return rows.filter((v) => !!v.liberacao?.isServiceProvider);
}
